import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import type { ObservationRecord } from '../types/observations';
import { listObservationsForUser } from '../services/observationService';
import {
  WEARABLE_METRIC_DEFINITIONS,
  type WearableMetricId,
} from '../data/wearableMetrics';
import {
  buildWearableMetricDatasets,
  getWearableMetricDatasetSummary,
  getWearableMetricDateLabel,
} from '../services/memberWearableMetricsService';
import WearableMetricChart from '../components/dashboard/WearableMetricChart';

function isWearableMetricId(value?: string): value is WearableMetricId {
  return Boolean(value && value in WEARABLE_METRIC_DEFINITIONS);
}

export default function WearableMetricDetailPage() {
  const { currentUser } = useAuth();
  const { metricId } = useParams<{ metricId: string }>();
  const [loading, setLoading] = useState(true);
  const [observations, setObservations] = useState<ObservationRecord[]>([]);

  useEffect(() => {
    const load = async () => {
      if (!currentUser || !isWearableMetricId(metricId)) {
        setLoading(false);
        return;
      }
      try {
        const wearableObservations = await listObservationsForUser(currentUser.uid, 'wearable');
        setObservations(wearableObservations);
      } catch (error) {
        console.error('Error loading wearable metric detail:', error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUser, metricId]);

  const dataset = useMemo(() => {
    if (!isWearableMetricId(metricId)) return null;
    return buildWearableMetricDatasets(observations)[metricId];
  }, [metricId, observations]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isWearableMetricId(metricId) || !dataset) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <section className="rounded-3xl border p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <h1 className="text-3xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
            Metric not found
          </h1>
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
            That wearable metric does not exist in the current Melio dashboard.
          </p>
          <Link to="/dashboard" className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
            Back to dashboard
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <section className="rounded-3xl border p-8 md:p-10 mb-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
              Wearable metric detail
            </p>
            <h1 className="text-3xl md:text-4xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
              {dataset.definition.label}
            </h1>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              {dataset.definition.description}
            </p>
          </div>
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
          >
            Back to dashboard
          </Link>
        </div>
      </section>

      <section className="grid lg:grid-cols-[1.15fr_0.85fr] gap-6 mb-8">
        <div className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Latest reading
              </p>
              <div className="text-4xl font-semibold" style={{ color: 'var(--melio-charcoal)' }}>
                {dataset.latestValueDisplay}
              </div>
            </div>
            <span className="rounded-full px-3 py-1 text-sm" style={{ backgroundColor: 'rgba(79, 93, 24,0.14)', color: 'var(--melio-sage)' }}>
              {dataset.deltaLabel}
            </span>
          </div>

          <WearableMetricChart dataset={dataset} />

          <p className="text-sm leading-relaxed mt-5" style={{ color: 'var(--melio-text-muted)' }}>
            {getWearableMetricDatasetSummary(dataset)}
          </p>
        </div>

        <div className="space-y-6">
          <section className="rounded-3xl border p-6 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
            <p className="text-xs uppercase tracking-wide mb-3" style={{ color: 'var(--melio-text-muted)' }}>
              Melio summary
            </p>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-charcoal)' }}>
              {dataset.definition.melioRead}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              {dataset.definition.caution}
            </p>
          </section>

          <section className="rounded-3xl border p-6 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
            <p className="text-xs uppercase tracking-wide mb-3" style={{ color: 'var(--melio-text-muted)' }}>
              Data freshness
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-charcoal)' }}>
              Latest parsed observation: {getWearableMetricDateLabel(dataset.latestObservedAt)}
            </p>
            {dataset.averageValueDisplay && (
              <p className="text-sm leading-relaxed mt-2" style={{ color: 'var(--melio-text-muted)' }}>
                Current window average: {dataset.averageValueDisplay}
              </p>
            )}
          </section>
        </div>
      </section>

      <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
              Clinical literature
            </p>
            <h2 className="text-2xl font-normal" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
              Suggested reading for this metric
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {dataset.definition.literatureLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border p-4 transition-opacity hover:opacity-90"
              style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)', backgroundColor: 'var(--b-bone)' }}
            >
              <div className="font-medium mb-2">{link.label}</div>
              <div className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                Open external literature
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
